import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Search, ChevronDown, X, Check } from 'lucide-react';

interface MultiSelectProps {
  options: { id: string; name: string }[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder: string;
  label?: string;
  searchable?: boolean;
}

export const MultiSelect: React.FC<MultiSelectProps> = ({ 
  options = [], 
  value = [], 
  onChange, 
  placeholder,
  label,
  searchable = true
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    const normalizedSearch = searchTerm.toLowerCase().trim();
    return options.filter(o => o.name.toLowerCase().includes(normalizedSearch));
  }, [options, searchTerm]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOption = (id: string) => {
    if (value.includes(id)) {
      onChange(value.filter(v => v !== id));
    } else {
      onChange([...value, id]);
    }
  };

  const isSelected = (id: string) => value.includes(id);

  return (
    <div className="relative w-full" ref={containerRef}>
      {label && <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-1.5">{label}</label>}
      
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-4 py-2.5 bg-white border rounded text-[10px] font-bold uppercase transition-all shadow-sm
          ${isOpen ? 'border-slate-900 ring-4 ring-slate-100' : 'border-slate-200 hover:border-slate-300'}`}
      >
        <div className="flex flex-wrap gap-1 items-center overflow-hidden">
          {value.length === 0 ? (
            <span className="text-slate-400">{placeholder}</span>
          ) : (
            <div className="flex items-center gap-1">
              <span className="text-slate-900 font-black">{value.length} Selected</span>
              <div className="flex -space-x-1">
                {value.slice(0, 3).map((v, i) => (
                  <div key={v} className="w-2 h-2 rounded-full bg-blue-500 border border-white" style={{ zIndex: 10 - i }} />
                ))}
              </div>
            </div>
          )}
        </div>
        <ChevronDown size={14} className={`text-slate-400 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-[100] w-full mt-2 bg-white border border-slate-900 rounded-lg shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 origin-top">
          {searchable && (
            <div className="p-3 border-b border-slate-100 bg-slate-50">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                <input
                  autoFocus
                  type="text"
                  placeholder="Filter options..."
                  className="w-full pl-9 pr-8 py-2.5 bg-white border border-slate-200 rounded-md font-bold text-[10px] outline-none focus:border-slate-900 uppercase tracking-tight"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          )}
          
          <div className="max-h-64 overflow-y-auto overflow-x-hidden p-1 custom-scrollbar">
            <div className="flex items-center justify-between p-2 mb-1">
              <button 
                onClick={() => onChange(options.map(o => o.id))}
                className="text-[9px] font-black text-blue-600 uppercase tracking-widest hover:underline"
              >
                Select All
              </button>
              <button 
                onClick={() => onChange([])}
                className="text-[9px] font-black text-rose-500 uppercase tracking-widest hover:underline"
              >
                Clear
              </button>
            </div>
            
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-8 text-center text-[10px] font-bold text-slate-300 uppercase italic tracking-widest">
                No matching options
              </div>
            ) : (
              <div className="space-y-0.5">
                {filteredOptions.map(option => (
                  <button
                    key={option.id}
                    onClick={() => toggleOption(option.id)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 rounded text-[10px] font-bold uppercase transition-all text-left
                      ${isSelected(option.id) ? 'bg-slate-900 text-white font-black' : 'hover:bg-slate-50 text-slate-600 hover:translate-x-1'}`}
                  >
                    <span className="truncate">{option.name}</span>
                    {isSelected(option.id) && <Check size={14} className="shrink-0 ml-2 animate-in zoom-in duration-300" />}
                    {!isSelected(option.id) && <div className="w-4 h-4 border-2 border-slate-200 rounded flex items-center justify-center" />}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
